import React, {useCallback, useMemo, useRef} from 'react';
import {Transition} from 'react-transition-group';

import throttle from 'lodash.throttle';

import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './slide-box.module.styl';

import type {ComponentProps, FC, ReactNode} from 'react';

const classNames = withCssModule(styles);

const SLIDE_BOX_CLASS_NAME = 'slide-box';
const SLIDE_BOX_ANIMATION_TIMEOUT = 300;
const TICK = 17;

export interface SlideBoxButtonProps {
    /**
     * Toggle handler forwarded from the parent (e.g. a `setShown` flip). The button
     * wraps this in a throttle so rapid clicks during an animation are ignored.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClick: (...args: any[]) => any,
    /**
     * Render prop that receives the throttled click handler and returns the trigger
     * element. Spread the handler onto whichever element should open/close the box.
     *
     * @example
     * ```tsx
     * <SlideBoxButton onClick={() => setShown(s => !s)}>
     *   {click => <button onClick={click}>Toggle</button>}
     * </SlideBoxButton>
     * ```
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: (onClick: (...args: any[]) => any) => ReactNode,
    /**
     * Milliseconds used as the throttle window. Should match the `timeout` prop
     * passed to the paired `<SlideBox>` so the throttle expires once the animation
     * finishes. Defaults to `300`.
     */
    timeout?: number,
}

/**
 * Wrapper that throttles rapid toggle clicks so the paired `<SlideBox>` animation
 * is never interrupted mid-flight.
 *
 * Accepts a render-prop `children` and passes back a throttled version of `onClick`.
 * Spread that handler onto the trigger element returned by the render prop.
 */
export const SlideBoxButton: FC<SlideBoxButtonProps> = ({
    onClick,
    children,
    timeout = SLIDE_BOX_ANIMATION_TIMEOUT,
}) => {
    const throttledOnClick = useMemo(
        () => throttle(onClick, timeout + 50),
        [onClick, timeout],
    );

    return (
        <>
            {children(throttledOnClick)}
        </>
    );
};

export type DefaultDivProps = ComponentProps<'div'>;
export interface SlideBoxProps extends DefaultDivProps {
    /**
     * Content rendered inside the collapsible panel. Mounted when `shown` becomes
     * `true` and unmounted after the collapse animation completes.
     */
    children: ReactNode,
    /**
     * When `true`, the panel slides open; when `false`, it slides closed and
     * unmounts after the animation finishes.
     */
    shown: boolean,
    /**
     * Called at the very start of both the expand and collapse animations, before
     * any height transition begins.
     */
    onAnimationStart?: () => void,
    /**
     * Called after the expand or collapse animation has fully completed and height
     * has settled (to `"auto"` on expand, or the element has been unmounted on collapse).
     */
    onAnimationEnd?: () => void,
    /**
     * Callback that receives the wrapper `<div>` DOM node (or `null` when unmounted).
     * Use to imperatively measure or scroll to the element.
     */
    getSlideBox?: (el: HTMLElement | null) => void,
    /**
     * Duration of the slide animation in milliseconds. Pass `0` to disable
     * animation entirely (the `slide-box_no-animation` modifier class is applied).
     * Defaults to `300`.
     */
    timeout?: number,
    /**
     * Extra class names merged onto the wrapper `<div>`.
     */
    className?: string,
}

/**
 * Animated expand/collapse container that slides content in and out vertically.
 *
 * When `shown` changes to `true` the element mounts, animates from `height: 0`
 * to its natural height, then resets to `height: auto`. When `shown` changes to
 * `false` the reverse animation runs before the element unmounts. The animation
 * duration is controlled by `timeout` (default 300 ms); set `timeout={0}` to
 * disable animation entirely.
 *
 * Pair with `SlideBoxButton`, which throttles rapid toggle clicks to prevent
 * animation glitches, and use `aria-expanded` / `aria-controls` on the trigger
 * button to satisfy accessibility requirements.
 */
export const SlideBox: FC<SlideBoxProps> = ({
    children,
    shown,
    getSlideBox,
    onAnimationStart,
    onAnimationEnd,
    timeout = SLIDE_BOX_ANIMATION_TIMEOUT,
    className,
    ...props
}) => {
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const classNameBase = SLIDE_BOX_CLASS_NAME;

    // Animation handlers
    const handleOnEnter = useCallback(() => {
        const node = nodeRef.current;
        if (node) {
            const height = `${node.clientHeight}px`;
            node.style.height = '0';
            node.style.overflow = 'hidden';
            // Use the smaller of TICK or timeout to avoid race conditions
            const delay = Math.min(TICK, timeout);
            setTimeout(() => {
                if (node) {
                    node.style.height = height;
                }
            }, delay);
            onAnimationStart?.();
        }
    }, [onAnimationStart, timeout]);

    const handleEntered = useCallback(() => {
        const node = nodeRef.current;
        if (node) {
            node.style.height = 'auto';
            node.style.overflow = 'visible';
            onAnimationEnd?.();
        }
    }, [onAnimationEnd]);

    const handleOnExit = useCallback(() => {
        const node = nodeRef.current;
        if (node) {
            node.style.height = `${node.clientHeight}px`;
            // Use the smaller of TICK or timeout to avoid race conditions
            const delay = Math.min(TICK, timeout);
            setTimeout(() => {
                if (node) {
                    node.style.height = '0';
                    node.style.overflow = 'hidden';
                }
            }, delay);
            onAnimationStart?.();
        }
    }, [onAnimationStart, timeout]);

    const handleExited = useCallback(() => {
        onAnimationEnd?.();
    }, [onAnimationEnd]);

    const classes = classNames(
        classNameBase,
        className,
        {
            [`${classNameBase}_no-animation`]: timeout === 0,
        },
    );

    return (
        <Transition
            nodeRef={nodeRef}
            mountOnEnter={true}
            unmountOnExit={true}
            appear={true}
            onEnter={handleOnEnter}
            onEntered={handleEntered}
            onExit={handleOnExit}
            onExited={handleExited}
            timeout={timeout}
            in={shown}>
            <div
                {...props}
                className={classes}
                ref={el => {
                    getSlideBox?.(el);
                    nodeRef.current = el;
                }}>
                {children}
            </div>
        </Transition>
    );
};

